import { useEffect, useRef } from "react";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { GENIE_ANIMATION_MODE } from "@/constants/genieAnimation";
import { useGenieMagicPathAnimation } from "@/hooks/useGenieMagicPathAnimation";
import { setGenieTravellerLive, whenBasePoseReady } from "@/lib/genieHeroInit";
import { createPoseController } from "@/lib/geniePose";
import { POSES, WAYPOINTS, targetFor } from "@/lib/genieWaypoints";

gsap.registerPlugin(ScrollTrigger);

const Sparkle = ({ className, style }) => (
    <svg viewBox="0 0 24 24" fill="#FDB927" className={className} style={style} aria-hidden="true">
        <path d="M12 1.5 14.6 9.4 22.5 12 14.6 14.6 12 22.5 9.4 14.6 1.5 12 9.4 9.4Z" />
    </svg>
);

export const GenieMascot = () => {
    const ref = useRef(null);
    const posesRef = useRef([]);
    const brokenRef = useRef({});
    const isCurrentMode = GENIE_ANIMATION_MODE === "current";

    useGenieMagicPathAnimation(ref, posesRef, brokenRef, GENIE_ANIMATION_MODE === "magic-path");

    useEffect(() => {
        if (!isCurrentMode) return undefined;
        if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return undefined;
        const el = ref.current;
        if (!el) return undefined;
        // Hero uses the in-layout static pose. Overlay stays off until Services.
        setGenieTravellerLive(false);
        gsap.set(el, { opacity: 0 });

        let triggers = [];
        let active = null;
        let wasMobile = window.innerWidth < 768;

        const poseCtrl = createPoseController(posesRef, brokenRef, { duration: 0.45 });

        const moveOpts = { duration: 1.8, ease: "power3.out" };
        const moveX = gsap.quickTo(el, "x", moveOpts);
        const moveY = gsap.quickTo(el, "y", moveOpts);
        const moveScale = gsap.quickTo(el, "scale", moveOpts);
        const moveRotation = gsap.quickTo(el, "rotation", moveOpts);

        const appear = (wp, instant = false) => {
            active = wp;

            if (wp.initial) {
                setGenieTravellerLive(false);
                gsap.set(el, { opacity: 0 });
                poseCtrl.setPose(wp.pose, true);
                return;
            }

            setGenieTravellerLive(true);
            poseCtrl.setPose(wp.pose, instant);
            const t = targetFor(el, wp);

            if (instant) {
                gsap.set(el, { x: t.x, y: t.y, scale: t.scale, rotation: t.rotation, opacity: 1 });
                return;
            }

            if (gsap.getProperty(el, "opacity") < 1) {
                gsap.to(el, { opacity: 1, duration: 1.05, ease: "power2.out", overwrite: "auto" });
            }

            moveX(t.x);
            moveY(t.y);
            moveScale(t.scale);
            moveRotation(t.rotation);
        };

        const disappear = (dir = 1) => {
            active = null;
            gsap.to(el, { opacity: 0, y: `+=${46 * dir}`, scale: "-=0.05", duration: 0.6, ease: "power2.in", overwrite: "auto" });
        };

        const heroWp = WAYPOINTS.find((wp) => wp.initial);

        const initHeroStatic = () => {
            whenBasePoseReady(posesRef, () => {
                if (heroWp) {
                    appear(heroWp, true);
                }
            });
        };

        const setup = () => {
            triggers.forEach((t) => t.kill());
            triggers = [];
            const mob = window.innerWidth < 768;
            WAYPOINTS.forEach((wp) => {
                if (mob && !wp.mobile) return;
                const node = document.querySelector(wp.id);
                if (!node) return;
                if (wp.initial) {
                    triggers.push(
                        ScrollTrigger.create({
                            trigger: node,
                            start: "top top",
                            end: "bottom 58%",
                            onEnterBack: () => appear(wp),
                        }),
                    );
                } else if (wp.stay) {
                    triggers.push(
                        ScrollTrigger.create({
                            trigger: node,
                            start: "top 62%",
                            end: "bottom top",
                            onEnter: () => appear(wp),
                            onEnterBack: () => appear(wp),
                        }),
                    );
                } else {
                    triggers.push(
                        ScrollTrigger.create({
                            trigger: node,
                            start: "top 62%",
                            end: "bottom 38%",
                            onEnter: () => appear(wp),
                            onEnterBack: () => appear(wp),
                        }),
                    );
                }
            });
        };

        initHeroStatic();
        setup();

        let resizeTimer = null;
        const onResize = () => {
            clearTimeout(resizeTimer);
            resizeTimer = setTimeout(() => {
                const nowMobile = window.innerWidth < 768;
                if (nowMobile !== wasMobile) {
                    wasMobile = nowMobile;
                    gsap.set(el, { opacity: 0 });
                    active = null;
                    setup();
                    initHeroStatic();
                    ScrollTrigger.refresh();
                } else if (active) {
                    appear(active);
                }
            }, 250);
        };
        window.addEventListener("resize", onResize);

        return () => {
            window.removeEventListener("resize", onResize);
            clearTimeout(resizeTimer);
            triggers.forEach((t) => t.kill());
            poseCtrl.killAll();
            gsap.killTweensOf(el);
            setGenieTravellerLive(false);
        };
    }, [isCurrentMode]);

    return (
        <div id="genie-traveller" ref={ref} data-testid="genie-traveller" aria-hidden="true">
            <div className="relative">
                <div className="genie-inner relative">
                    {POSES.map((src, i) => (
                        <img
                            key={src}
                            src={src}
                            alt=""
                            draggable="false"
                            ref={(node) => {
                                posesRef.current[i] = node;
                            }}
                            onError={() => {
                                brokenRef.current[i] = true;
                                if (posesRef.current[i]) posesRef.current[i].style.display = "none";
                            }}
                            className={`genie-pose block w-full ${i === 0 ? "relative h-auto" : "absolute inset-0 h-full object-contain"}`}
                            style={{ opacity: i === 0 ? 1 : 0 }}
                        />
                    ))}
                </div>
                <Sparkle className="genie-sparkle -left-3 top-[16%] h-5 w-5" style={{ animationDelay: "0.2s" }} />
                <Sparkle className="genie-sparkle -right-2 top-[38%] h-4 w-4" style={{ animationDelay: "1.1s" }} />
                <Sparkle className="genie-sparkle bottom-[6%] left-[12%] h-3.5 w-3.5" style={{ animationDelay: "1.9s" }} />
            </div>
        </div>
    );
};
